import type { Layout } from '../../data/types';
import type { Vec2 } from '../../geo/types';
import { distancePointSegment, pointInRing } from '../../geo/polygon';
import type { BakedCrowd } from './crowd/crowdPlan';
import type { Ground } from './crowd/walkNet';
import { cargoDeliveryLoop } from './cargoPlan';
import type { LifeEntrance } from './types';

export interface NeighbourhoodPlay {
  readonly streetId: string;
  readonly name: string;
  readonly home: LifeEntrance;
  readonly center: Vec2;
  readonly heading: number;
  readonly route: readonly Vec2[];
}

/** Play belongs beside inhabited small streets, with no route through the trading squares. */
export function planNeighbourhoodPlay(
  layout: Layout,
  crowd: BakedCrowd,
  ground: Ground
): NeighbourhoodPlay[] {
  const homes = (crowd.doorways ?? []).filter(
    (door): door is LifeEntrance => door?.kind === 'home'
  );
  const outsideSquares = (p: Vec2) =>
    layout.squares.every(
      (square) =>
        !pointInRing(p, square.polygon) &&
        square.polygon.every(
          (a, i) =>
            distancePointSegment(p, {
              a,
              b: square.polygon[(i + 1) % square.polygon.length]!,
            }) > 4
        )
    );
  const clear = (p: Vec2, radius: number): boolean => {
    for (let i = 0; i < 16; i++) {
      const a = (i * Math.PI) / 8;
      const edge: Vec2 = [p[0] + Math.cos(a) * radius, p[1] + Math.sin(a) * radius];
      if (!ground.paved(edge) || !outsideSquares(edge)) return false;
    }
    return ground.paved(p);
  };
  const candidates: NeighbourhoodPlay[] = [];
  for (const street of layout.streets) {
    if (street.kind === 'quay' || street.width < 3.5 || street.width > 5) continue;
    const neighbours = homes.filter((home) => home.streetId === street.id);
    if (!neighbours.length) continue;
    for (let i = 1; i < street.centerline.length; i++) {
      const a = street.centerline[i - 1]!,
        b = street.centerline[i]!;
      const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (length < 24) continue;
      const dx = (b[0] - a[0]) / length,
        dy = (b[1] - a[1]) / length;
      for (const t of [0.5, 0.35, 0.65]) {
        const center: Vec2 = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
        const home = [...neighbours].sort(
          (h, k) =>
            Math.hypot(h.position[0] - center[0], h.position[1] - center[1]) -
            Math.hypot(k.position[0] - center[0], k.position[1] - center[1])
        )[0]!;
        if (
          Math.hypot(home.position[0] - center[0], home.position[1] - center[1]) > 15 ||
          !clear(center, 1.4)
        )
          continue;
        const route = cargoDeliveryLoop(
          [
            [center[0] - dx * 6, center[1] - dy * 6],
            [center[0] + dx * 6, center[1] + dy * 6],
          ],
          0.55
        );
        if (!route.every((p) => clear(p, 0.55))) continue;
        candidates.push({
          streetId: street.id,
          name: street.name,
          home,
          center,
          heading: Math.atan2(dy, dx),
          route,
        });
        break;
      }
    }
  }
  const chosen: NeighbourhoodPlay[] = [];
  while (chosen.length < 10) {
    const remaining = candidates.filter((p) => chosen.every((q) => p.streetId !== q.streetId));
    const separation = (p: NeighbourhoodPlay) =>
      chosen.length
        ? Math.min(
            ...chosen.map((q) => Math.hypot(p.center[0] - q.center[0], p.center[1] - q.center[1]))
          )
        : Math.hypot(...p.center);
    remaining.sort((a, b) => separation(b) - separation(a));
    if (!remaining[0]) break;
    chosen.push(remaining[0]);
  }
  return chosen;
}
